// =====================================================================
//  ADMIN CONSOLE, OVERVIEW (COMMAND CENTER)
// ---------------------------------------------------------------------
//  The first screen an administrator sees. It answers, in order:
//
//    1. What needs me right now?   (the approval queue, first and loudest)
//    2. What is the state of the org today?  (KPI strip)
//    3. Where is it trending?      (headcount / attendance / payroll)
//    4. What just happened?        (activity feed)
//
//  Deliberately NOT a set of gradient tiles. Every number here is server-
//  computed from one `/admin/overview` call, and every tile drills into
//  the screen that can act on it.
// =====================================================================

import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ArrowRight,
  CalendarDays,
  Check,
  CheckCheck,
  Clock,
  FileText,
  LifeBuoy,
  ShieldCheck,
  TrendingUp,
  UserPlus,
  Users,
  Wallet,
  X,
} from 'lucide-react';

import backendApi from '../../api/backendApi';
import {
  Button,
  ChartTooltip,
  Chip,
  EmptyState,
  Grid,
  KpiTile,
  LoadingBlock,
  Notice,
  Panel,
  PanelBody,
  PanelHead,
  StackBar,
  StatusChip,
  toast,
} from '../ui';
import { CHART_SEQUENCE, series } from '../theme';
import { fmtDate, fmtMoney, fmtNumber, fmtPercent, timeAgo, titleCase } from '../format';

const KIND_ICON = {
  leave: CalendarDays,
  access_request: ShieldCheck,
  deletion_request: ShieldCheck,
  ticket: LifeBuoy,
  employee: Users,
  document: FileText,
  performance: TrendingUp,
  candidate: UserPlus,
};

const KIND_ROUTE = {
  leave: 'leave',
  access_request: 'access',
  deletion_request: 'access',
  ticket: 'helpdesk',
  employee: 'people',
  document: 'documents',
  performance: 'performance',
  candidate: 'recruitment',
};

export default function OverviewPage({ navigate, can }) {
  const queryClient = useQueryClient();
  const canDecide = can('leave.approve');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: () => backendApi.admin.overview(),
    refetchInterval: 120_000,
  });

  const { data: approvals = [], isLoading: approvalsLoading } = useQuery({
    queryKey: ['admin', 'approvals'],
    queryFn: () => backendApi.admin.approvals(),
    refetchInterval: 60_000,
  });

  const { data: activity = [] } = useQuery({
    queryKey: ['admin', 'activity', 14],
    queryFn: () => backendApi.admin.activity(14),
  });

  const { data: payrollTrend = [] } = useQuery({
    queryKey: ['admin', 'payroll-trend', 12],
    queryFn: () => backendApi.admin.payrollTrend(12),
  });

  const decide = useMutation({
    mutationFn: ({ id, decision }) => backendApi.admin.decideLeave(id, decision),
    onSuccess: (_row, { decision }) => {
      toast.success(`Leave ${decision === 'approve' ? 'approved' : 'rejected'}`);
      queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
    onError: (err) => toast.error(err.message || 'Could not record that decision'),
  });

  if (isLoading && !data) {
    return (
      <div className="ac-page">
        <LoadingBlock label="Loading organization data…" height={320} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="ac-page">
        <Notice tone="negative" title="Could not load the overview">
          The organization summary is unavailable. Check that the API is reachable, then refresh.
        </Notice>
      </div>
    );
  }

  const { headcount, attendanceToday, payroll, approvals: counts, departments, headcountTrend, attendanceTrend, tickets, performance, recruitment } = data;

  // Only weekdays carry signal for an attendance chart; weekends would
  // otherwise read as a company-wide absence every seven days.
  const workdays = (attendanceTrend || []).filter((d) => !d.isWeekend);
  const recentWorkdays = workdays.slice(-14);

  return (
    <div className="ac-page">
      {/* ---------------- KPI STRIP ---------------- */}
      <Grid cols={4} style={{ marginBottom: 14 }}>
        <KpiTile
          label="Headcount"
          value={fmtNumber(headcount.active)}
          unit={headcount.inactive > 0 ? `of ${headcount.total}` : undefined}
          icon={Users}
          delta={headcount.joinersDelta}
          spark={(headcountTrend || []).map((m) => m.headcount)}
          onClick={() => navigate('people')}
          foot={`${headcount.joinedThisMonth} joined this month`}
        />
        <KpiTile
          label="Present today"
          value={fmtPercent(attendanceToday.presentRate)}
          icon={Clock}
          tone={attendanceToday.presentRate >= 85 ? 'pos' : 'warn'}
          onClick={() => navigate('attendance')}
          foot={`${attendanceToday.present} in · ${attendanceToday.onLeave} on leave · ${attendanceToday.absent} absent`}
        />
        <KpiTile
          label="Awaiting decision"
          value={fmtNumber(counts.total)}
          icon={ShieldCheck}
          tone={counts.total > 0 ? 'warn' : 'pos'}
          onClick={() => navigate('leave')}
          foot={
            counts.total === 0
              ? 'Everything is cleared'
              : `${counts.leave} leave · ${counts.accessRequests + counts.deletionRequests} access · ${counts.openTickets} tickets`
          }
        />
        <KpiTile
          label={`Payroll · ${payroll.month} ${payroll.year}`}
          value={fmtMoney(payroll.netCurrent, { compact: true })}
          icon={Wallet}
          delta={payroll.netDelta}
          spark={(payrollTrend || []).map((m) => m.net)}
          onClick={() => navigate('payroll')}
          foot={payroll.hasRunThisMonth ? `${payroll.payslipsCurrent} payslips issued` : 'Not run yet this month'}
        />
      </Grid>

      {/* ---------------- ACTION QUEUE ---------------- */}
      <Panel style={{ marginBottom: 14 }}>
        <PanelHead
          title="Needs your attention"
          sub={
            approvals.length === 0
              ? 'Nothing is waiting on you.'
              : `${approvals.length} item${approvals.length === 1 ? '' : 's'} awaiting a decision`
          }
          actions={
            approvals.length > 0 ? (
              <Button size="sm" iconRight={ArrowRight} onClick={() => navigate('leave')}>
                Open queue
              </Button>
            ) : null
          }
        />
        <PanelBody flush>
          {approvalsLoading ? (
            <LoadingBlock label="Loading queue…" height={140} />
          ) : approvals.length === 0 ? (
            <EmptyState
              icon={CheckCheck}
              title="Queue is clear"
              text="Leave requests, access requests and open tickets will appear here as they arrive."
            />
          ) : (
            approvals.slice(0, 6).map((item) => {
              const Icon = KIND_ICON[item.kind] || ShieldCheck;
              const isLeave = item.kind === 'leave';
              return (
                <div className="ac-queue-item" key={`${item.kind}-${item.id}`}>
                  <span className="ac-feed-icon">
                    <Icon size={13} strokeWidth={2} />
                  </span>

                  <div className="ac-queue-body">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ac-ink)' }}>{item.title}</span>
                      <Chip tone={item.kind === 'ticket' ? 'info' : 'warning'}>
                        {titleCase(item.kind.replace('_', ' '))}
                      </Chip>
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--ac-muted)', marginTop: 1 }}>{item.subtitle}</div>
                    {item.detail ? (
                      <div style={{ fontSize: 11.5, color: 'var(--ac-faint)', marginTop: 2, fontStyle: 'italic' }}>
                        “{item.detail}”
                      </div>
                    ) : null}
                  </div>

                  <span style={{ fontSize: 11, color: 'var(--ac-faint)', flex: '0 0 auto' }}>
                    {timeAgo(item.created_at)}
                  </span>

                  <div className="ac-queue-actions">
                    {isLeave && canDecide ? (
                      <>
                        <Button
                          size="sm"
                          variant="accent"
                          icon={Check}
                          disabled={decide.isPending}
                          onClick={() => decide.mutate({ id: item.id, decision: 'approve' })}
                        >
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          icon={X}
                          disabled={decide.isPending}
                          onClick={() => decide.mutate({ id: item.id, decision: 'reject' })}
                        >
                          Reject
                        </Button>
                      </>
                    ) : (
                      <Button size="sm" iconRight={ArrowRight} onClick={() => navigate(KIND_ROUTE[item.kind] || 'overview')}>
                        Review
                      </Button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </PanelBody>
      </Panel>

      {/* ---------------- TRENDS ---------------- */}
      <Grid cols={2} style={{ marginBottom: 14 }}>
        <Panel>
          <PanelHead title="Headcount" sub="Cumulative active people, last 12 months" />
          <PanelBody>
            <ResponsiveContainer width="100%" height={210}>
              <AreaChart data={headcountTrend} margin={{ top: 6, right: 6, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="ac-hc" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={series.accent} stopOpacity={0.24} />
                    <stop offset="100%" stopColor={series.accent} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="label" tickLine={false} axisLine={false} />
                <YAxis tickLine={false} axisLine={false} width={44} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Area
                  type="monotone"
                  dataKey="headcount"
                  name="Headcount"
                  stroke={series.accent}
                  strokeWidth={2}
                  fill="url(#ac-hc)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </PanelBody>
        </Panel>

        <Panel>
          <PanelHead
            title="Attendance"
            sub="Present vs absent, last 14 working days"
            actions={
              <Button size="sm" variant="ghost" iconRight={ArrowRight} onClick={() => navigate('attendance')}>
                Register
              </Button>
            }
          />
          <PanelBody>
            {recentWorkdays.length === 0 ? (
              <EmptyState icon={Clock} title="No attendance yet" text="Punch-in data will appear here once employees start clocking in." />
            ) : (
              <ResponsiveContainer width="100%" height={210}>
                <BarChart data={recentWorkdays} margin={{ top: 6, right: 6, left: -20, bottom: 0 }} barCategoryGap="22%">
                  <CartesianGrid strokeDasharray="2 4" vertical={false} />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} interval="preserveStartEnd" />
                  <YAxis tickLine={false} axisLine={false} width={44} allowDecimals={false} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(15,20,25,0.04)' }} />
                  <Bar dataKey="present" name="Present" stackId="a" fill={series.present} radius={[0, 0, 0, 0]} />
                  <Bar dataKey="onLeave" name="On leave" stackId="a" fill={series.leave} />
                  <Bar dataKey="absent" name="Absent" stackId="a" fill={series.absent} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </PanelBody>
        </Panel>
      </Grid>

      {/* ---------------- DEPARTMENTS + PAYROLL ---------------- */}
      <Grid cols={2} style={{ marginBottom: 14 }}>
        <Panel>
          <PanelHead
            title="Departments"
            sub={`${departments.length} department${departments.length === 1 ? '' : 's'} · derived from employee records`}
            actions={
              <Button size="sm" variant="ghost" iconRight={ArrowRight} onClick={() => navigate('organization')}>
                Manage
              </Button>
            }
          />
          <PanelBody flush>
            {departments.length === 0 ? (
              <EmptyState icon={Users} title="No departments yet" text="Assign departments on employee records to see them grouped here." />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: 14 }}>
                <ResponsiveContainer width={168} height={168}>
                  <PieChart>
                    <Pie
                      data={departments.slice(0, 7)}
                      dataKey="headcount"
                      nameKey="department"
                      innerRadius={46}
                      outerRadius={76}
                      paddingAngle={2}
                      stroke="none"
                    >
                      {departments.slice(0, 7).map((entry, index) => (
                        <Cell key={entry.department} fill={CHART_SEQUENCE[index % CHART_SEQUENCE.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} />
                  </PieChart>
                </ResponsiveContainer>

                <div style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 7 }}>
                  {departments.slice(0, 6).map((dept, index) => (
                    <div key={dept.department} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 2,
                          flex: '0 0 8px',
                          background: CHART_SEQUENCE[index % CHART_SEQUENCE.length],
                        }}
                      />
                      <span
                        style={{
                          flex: '1 1 auto',
                          fontSize: 12,
                          color: 'var(--ac-body)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {dept.department}
                      </span>
                      <span className="ac-num" style={{ fontSize: 12, fontWeight: 650, color: 'var(--ac-ink)' }}>
                        {dept.headcount}
                      </span>
                      <span className="ac-num" style={{ fontSize: 11, color: 'var(--ac-faint)', width: 42, textAlign: 'right' }}>
                        {fmtPercent(dept.share, { decimals: 0 })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </PanelBody>
        </Panel>

        <Panel>
          <PanelHead
            title="Payroll cost"
            sub="Net payout per month"
            actions={
              <Button size="sm" variant="ghost" iconRight={ArrowRight} onClick={() => navigate('payroll')}>
                Payroll
              </Button>
            }
          />
          <PanelBody>
            {payrollTrend.every((m) => m.net === 0) ? (
              <EmptyState icon={Wallet} title="No payroll runs yet" text="Run payroll to start tracking monthly cost." />
            ) : (
              <ResponsiveContainer width="100%" height={210}>
                <BarChart data={payrollTrend} margin={{ top: 6, right: 6, left: -8, bottom: 0 }} barCategoryGap="26%">
                  <CartesianGrid strokeDasharray="2 4" vertical={false} />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} interval="preserveStartEnd" />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    width={52}
                    tickFormatter={(v) => fmtMoney(v, { compact: true })}
                  />
                  <Tooltip
                    content={<ChartTooltip formatter={(v) => fmtMoney(v)} />}
                    cursor={{ fill: 'rgba(15,20,25,0.04)' }}
                  />
                  <Bar dataKey="net" name="Net payout" fill={series.accent} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </PanelBody>
        </Panel>
      </Grid>

      {/* ---------------- SECONDARY STATS + ACTIVITY ---------------- */}
      <Grid cols={2}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Panel>
            <PanelHead title="Today at a glance" sub={fmtDate(data.date)} />
            <PanelBody>
              <StackBar
                segments={[
                  { value: attendanceToday.present - attendanceToday.late, color: series.present, label: 'On time' },
                  { value: attendanceToday.late, color: series.late, label: 'Late' },
                  { value: attendanceToday.onLeave, color: series.leave, label: 'On leave' },
                  { value: attendanceToday.absent, color: series.absent, label: 'Absent' },
                ]}
              />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginTop: 14 }}>
                <MiniStat label="On time" value={attendanceToday.present - attendanceToday.late} color={series.present} />
                <MiniStat label="Late" value={attendanceToday.late} color={series.late} />
                <MiniStat label="On leave" value={attendanceToday.onLeave} color={series.leave} />
                <MiniStat label="Absent" value={attendanceToday.absent} color={series.absent} />
                <MiniStat label="Still clocked in" value={attendanceToday.stillClockedIn} />
                <MiniStat label="Avg hours" value={attendanceToday.averageHours} suffix="h" />
              </div>
            </PanelBody>
          </Panel>

          <Grid cols={3}>
            <MicroPanel
              label="Open tickets"
              value={tickets.open}
              foot={tickets.highPriority > 0 ? `${tickets.highPriority} high priority` : 'None urgent'}
              onClick={() => navigate('helpdesk')}
            />
            <MicroPanel
              label="Avg rating"
              value={performance.averageRating || ','}
              foot={`${performance.reviews} review${performance.reviews === 1 ? '' : 's'}`}
              onClick={() => navigate('performance')}
            />
            <MicroPanel
              label="Candidates"
              value={recruitment.total}
              foot="In pipeline"
              onClick={() => navigate('recruitment')}
            />
          </Grid>
        </div>

        <Panel>
          <PanelHead
            title="Recent activity"
            sub="Latest records created across the organization"
            actions={
              <Button size="sm" variant="ghost" iconRight={ArrowRight} onClick={() => navigate('activity')}>
                All
              </Button>
            }
          />
          <PanelBody flush>
            {activity.length === 0 ? (
              <EmptyState title="No activity yet" text="Records created in the HRMS will show up here." />
            ) : (
              <div className="ac-feed">
                {activity.map((item) => {
                  const Icon = KIND_ICON[item.kind] || FileText;
                  return (
                    <div className="ac-feed-item" key={`${item.kind}-${item.id}`}>
                      <span className="ac-feed-icon">
                        <Icon size={13} strokeWidth={2} />
                      </span>
                      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                        <div className="ac-feed-title">{item.title}</div>
                        <div className="ac-feed-meta">
                          {titleCase(item.kind)}
                          {item.subtitle ? ` · ${item.subtitle}` : ''} · {timeAgo(item.created_at)}
                        </div>
                      </div>
                      {item.status ? <StatusChip value={item.status} dot={false} /> : null}
                    </div>
                  );
                })}
              </div>
            )}
          </PanelBody>
        </Panel>
      </Grid>
    </div>
  );
}

/* --------------------------- small helpers --------------------------- */

function MiniStat({ label, value, color, suffix = '' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
      {color ? <span style={{ width: 7, height: 7, borderRadius: 2, background: color, flex: '0 0 7px' }} /> : null}
      <span style={{ fontSize: 11.5, color: 'var(--ac-muted)', flex: '1 1 auto', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </span>
      <span className="ac-num" style={{ fontSize: 13, fontWeight: 700, color: 'var(--ac-ink)' }}>
        {value}
        {suffix}
      </span>
    </div>
  );
}

function MicroPanel({ label, value, foot, onClick }) {
  return (
    <button type="button" className="ac-kpi" onClick={onClick} style={{ gap: 5, padding: '11px 12px' }}>
      <span className="ac-kpi-label">{label}</span>
      <span className="ac-kpi-value" style={{ fontSize: 21 }}>
        {value}
      </span>
      <span className="ac-kpi-foot">{foot}</span>
    </button>
  );
}
